import dayjs from 'dayjs';
import { isString } from '@aviata/type-guards';

import { CALENDAR_MODES } from './config';
import type { CalendarMode } from './config';

type BuildNewDatesProps = {
  firstDate: string;
  secondDate: string;
  isSingleMode: boolean;
};

export const getCalendarDateLimits = (
  maxDateProp?: dayjs.ConfigType,
  minDateProp?: dayjs.ConfigType,
) => {
  const minDate = minDateProp ? dayjs(minDateProp) : dayjs();
  const maxDate = maxDateProp || minDate.add(1, 'y');
  const [min, max] = [minDate, dayjs(maxDate)].map(date =>
    date.format('YYYY-MM-DD'),
  );
  return { min, max };
};

export const scrollToSelectedDate = (
  calendarNode: HTMLElement,
  scrollElementSelector: string,
) => {
  const selectedDateNode = calendarNode.querySelector(
    '[data-is-selected="true"]',
  );
  if (selectedDateNode) {
    const monthNode = selectedDateNode.closest(scrollElementSelector);
    monthNode?.scrollIntoView();
  }
};

export const scrollToMonthOfDate = (
  calendarNode: HTMLElement,
  date: string,
) => {
  if (!date || !calendarNode) {
    return;
  }
  const target = dayjs(date);
  const monthKey = `${target.year()}-${target.month()}`;
  const monthEl = calendarNode.querySelector(`[data-month-key="${monthKey}"]`);
  monthEl?.scrollIntoView();
};

function isFirstDateExpired(firstDate: string, secondDate: string) {
  return dayjs(firstDate).isAfter(secondDate);
}

export function buildNewDates({
  firstDate,
  secondDate,
  isSingleMode,
}: BuildNewDatesProps) {
  if (!secondDate) {
    return [firstDate];
  }
  if (isFirstDateExpired(firstDate, secondDate)) {
    return isSingleMode ? [firstDate, firstDate] : [secondDate, secondDate];
  }
  return [firstDate, secondDate];
}

/**
 * Need to cast to string[] to use in Array.includes
 * @see https://github.com/microsoft/TypeScript/issues/26255
 */
const calendarModes = Object.values(CALENDAR_MODES) as string[];

export const isCalendarMode = (mode: unknown): mode is CalendarMode =>
  isString(mode) && calendarModes.includes(mode);

export const filterPricesByDate = (
  date: string,
  prices: Record<string, { price: string }> | null,
) => {
  if (!prices) return {};

  const filteredPrices = Object.entries(prices).filter(
    ([priceDate]) => new Date(priceDate).getTime() >= new Date(date).getTime(),
  );

  return Object.fromEntries(filteredPrices);
};
